import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, PlaneTakeoff } from 'lucide-react';

const BLUE = '#3C59B2';

const profiles = [
  {
    id: 'matterport-only',
    shortTitle: 'Matterport',
    fullTitle: 'Matterport',
    icon: Box,
    challenges: [
      'Listings need walkthrough videos to stand out',
      "You don't have the time or skills to edit video",
      'You want professional results without the learning curve',
    ],
    solutions: [
      'Send your Matterport link. We do the rest.',
      'Receive a professional walkthrough video : Branded or Unbranded',
      'Delivered in 24 hours (Mon–Thu)',
    ],
  },
  {
    id: 'matterport-drone',
    shortTitle: 'Matterport + drone clips',
    fullTitle: 'Matterport + drone clips',
    icon: PlaneTakeoff,
    challenges: [
      'You have a Matterport tour and drone clips',
      'You want one polished video montage, not raw footage',
      "Editing takes hours you'd rather spend on listings",
    ],
    solutions: [
      'Send your Matterport link + drone clips',
      'We create a full edited video montage',
      'Delivered in 24 hours (Mon–Thu)',
    ],
  },
] as const;

const ProblemSolution: React.FC = () => {
  const [activeProfile, setActiveProfile] = useState('matterport-only');
  const currentProfile = profiles.find((p) => p.id === activeProfile);

  return (
    <section className="py-12 sm:py-16 md:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10">
          <motion.h2
            className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Pick what you have. We handle the rest
          </motion.h2>
        </div>

        <div className="mx-auto max-w-5xl flex flex-col items-center gap-6">
          <div className="w-full flex flex-col items-center gap-3">
            <span className="text-base text-gray-500 text-center">You already have:</span>
            <div className="flex gap-2 flex-wrap justify-center">
              {profiles.map((profile) => {
                const active = activeProfile === profile.id;
                const Icon = profile.icon;
                return (
                  <button
                    key={profile.id}
                    type="button"
                    onClick={() => setActiveProfile(profile.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-base font-semibold border transition-all duration-150 ${
                      active ? 'text-white shadow-sm' : 'text-[#3C59B2] hover:bg-slate-50'
                    }`}
                    style={
                      active
                        ? { background: BLUE, borderColor: BLUE }
                        : { background: '#fff', borderColor: '#a0aec0' }
                    }
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                    <span className="sm:hidden">{profile.shortTitle}</span>
                    <span className="hidden sm:inline">{profile.fullTitle}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {currentProfile && (
            <motion.div
              className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 rounded-3xl border border-[#E2E8F0] overflow-hidden bg-white shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="p-6 sm:p-8 md:border-r border-[#E2E8F0]">
                <p className="text-sm font-bold text-red-500 uppercase tracking-wide mb-5 text-center">Your challenge</p>
                <ul className="flex flex-col gap-4">
                  {currentProfile.challenges.map((item, i) => (
                    <li key={i} className="flex gap-2.5 text-base text-gray-700 leading-relaxed">
                      <span className="text-red-400 mt-0.5 font-bold">✕</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-6 sm:p-8 bg-slate-50">
                <p className="text-sm font-bold text-green-600 uppercase tracking-wide mb-5 text-center">With 3DtoVideo</p>
                <ul className="flex flex-col gap-4">
                  {currentProfile.solutions.map((item, i) => (
                    <li key={i} className="flex gap-2.5 text-base text-gray-700 leading-relaxed">
                      <span className="text-green-600 mt-0.5 font-bold">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProblemSolution;
